import React, { useCallback, useMemo } from 'react'
import { Image } from 'Components/view-image'
import AuthorAvatar, {
  IProps as IAuthorAvatarProps,
} from 'Components/author/author-avatar'
import formatTime from 'lib/format-time'
import formatNumber from '@arsenal/arsenal/modules/formatNumber'
import FontSize10 from 'Components/font-size-10'
import formatDuration from 'lib/format-duration'
import classNames from 'classnames'
import liveLink from 'Components/new/links/live-link'
import anyLink from 'Components/new/links/any-link'
import articleLink from 'Components/new/links/article-link'
import groupLink from 'Components/new/links/group-link'
import useToggleCollect from 'Components/hooks/use-toggle-collect'
import {
  preventDefault,
  stopPropagation,
} from 'Components/new/community-card/interact'
import style from './index.module.scss'

export const CardCollectButton: React.FC<{
  id?: string
  collected?: boolean
  onClick?(status: boolean): void
  className?: string
}> = ({ id, collected, onClick, className }) => {
  const { update: toggle, curStatus } = useToggleCollect({
    id: id || '',
    collected,
  })

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(curStatus)
    }
    if (toggle) {
      toggle()
    }
  }, [onClick, toggle, curStatus])

  return (
    <React.Fragment>
      <button
        type={'button'}
        className={classNames(
          'collect-btn tw-hidden tw-absolute tw-z-10 tw-right-0 tw-top-0 tw-h-36 tw-rounded-bl-2 tw-bg-common-black tw-text-white tw-bg-opacity-50 hover:tw-bg-opacity-80',
          curStatus ? 'tw-w-80 tw-text-14' : 'tw-w-36',
          className
        )}
        onClick={stopPropagation(preventDefault(handleClick))}
      >
        {curStatus ? (
          '取消收藏'
        ) : (
          <i
            className="DCD_Icon icon_collect_24_normal"
            style={{ fontSize: 18 }}
          />
        )}
      </button>
    </React.Fragment>
  )
}

export const Duration: React.FC<{
  duration?: number | string
  className?: string
}> = ({ duration, className }) => {
  return duration ? (
    <div
      className={classNames(
        'tw-w-50 tw-h-20 tw-flex tw-items-center tw-justify-center tw-rounded-2 tw-bg-black tw-bg-opacity-80 tw-text-white tw-text-12',
        className
      )}
    >
      <FontSize10 width={44} height={10}>
        <i className="DCD_Icon icon_play_1 tw-text-16 tw-mr-2" />
        {typeof duration === 'string' ? duration : formatDuration(duration)}
      </FontSize10>
    </div>
  ) : null
}

interface IProps
  extends Partial<
    Pick<
      IAuthorAvatarProps,
      'userId' | 'avatar' | 'userVerified' | 'verifyType' | 'username'
    >
  > {
  type?: 'live' | 'article' | 'group' // 直播 ｜ 文章 视频 ｜ 节目
  contentType?: number // feed type // feed 里的type
  articleType?: number // article_type
  id?: string // 直播间id | 文章id
  webRid?: string // 抖音直播间路径id
  groupId?: string // 节目id
  groupName?: string // 节目名称
  total?: number // 节目期数
  coverUrl?: string // 封面url
  coverLazy?: boolean // 封面懒加载
  coverAlt?: string // 封面url alt
  coverWidth?: number // 图片宽度 用于切图优化
  coverHeight?: number // 图片高度 用于切图优化
  popularity?: number // 直播人气
  duration?: number | string // 视频时长
  title?: string // 标题
  watchCount?: number // 观看/阅读次数
  publishTime?: number | string // 发布时间 支持数字和字符串，数字时自动格式化
  collected?: boolean // 是否已收藏
  collectVisible?: boolean // 是否显示收藏
  zt?: string
  className?: string
  tag?: string
  onClick?(): void
  onClickGroup?(): void
  onClickUser?(): void
  onClickCollect?(collected: boolean): void
}

const CommonCard: React.FC<IProps> = ({
  type = 'article',
  id,
  webRid,
  contentType,
  articleType,
  groupId,
  groupName,
  userId,
  username,
  avatar,
  userVerified,
  verifyType,
  total,
  coverUrl = '',
  coverLazy = true,
  coverAlt,
  coverWidth = 250,
  coverHeight = 140,
  popularity = 0,
  duration,
  title,
  watchCount,
  publishTime,
  collected,
  collectVisible = true,
  zt,
  className,
  tag,
  onClick,
  onClickGroup,
  onClickUser,
  onClickCollect,
}) => {
  let isUgc = false
  if (contentType) {
    isUgc = contentType < 2000 || contentType >= 2300
  }
  const linkWrapper = useMemo(() => {
    if (type === 'live') {
      return liveLink({ id, webRid })
    } else if (typeof contentType !== 'undefined') {
      return articleLink({ id, zt, isVideo: Boolean(duration), isUgc, title })
    }
    return anyLink({
      id: id!,
      articleType,
      title,
      queryParams: typeof zt !== 'undefined' ? { zt: zt! } : null!,
    })
  }, [type, id, zt, duration])

  const header = useMemo(() => {
    return type === 'group' && groupId
      ? groupLink({ id: groupId })(
          <div
            className="tw-h-30 tw-flex tw-items-center tw-bg-white tw-px-8 tw-text-12 tw-text-common-gray tw-rounded-tl-2 tw-rounded-tr-2 hover:tw-bg-common-black hover:tw-text-common-white"
            onClick={onClickGroup}
          >
            <p
              className="tw-flex-1 line-1 tw-mr-6"
              style={{ color: 'inherit' }}
            >
              <span title={groupName}>{groupName}</span>
            </p>
            <div>
              共{total}期
              <i
                className="DCD_Icon icon_triangle_right"
                style={{ fontSize: 12 }}
              />
            </div>
          </div>
        )
      : null
  }, [type, groupId, groupName, total, onClickGroup])

  const liveTag = useMemo(() => {
    return (
      <React.Fragment>
        {type === 'live' ? (
          <div className="live tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-flex tw-rounded-br-2 tw-overflow-hidden">
            <div className="live-icon tw-w-60 tw-h-22 tw-flex tw-items-center tw-pl-22 tw-font-semibold">
              <FontSize10 width={30} height={14}>
                直播中
              </FontSize10>
            </div>
            <div className="tw-w-60 tw-h-22 tw-flex tw-items-center tw-justify-center tw-bg-common-black tw-bg-opacity-80 tw-text-white">
              <FontSize10 width={44} height={14}>
                {formatNumber(popularity)}人次
              </FontSize10>
            </div>
          </div>
        ) : null}
        <style jsx>
          {`
            .live-icon {
              background: url(${require('static/live-black.gif')}) 8px
                  center/14px 14px no-repeat,
                #ffcc32;
            }
          `}
        </style>
      </React.Fragment>
    )
  }, [type, popularity])

  const footer = useMemo(() => {
    return type === 'live' ? null : (
      <p className="tw-mt-8 tw-h-20 tw-lead tw-text-common-gray tw-text-14">
        {linkWrapper(
          <React.Fragment>
            {watchCount !== undefined ? (
              <span className="tw-mr-8">
                {formatNumber(watchCount)}次{duration ? '播放' : '阅读'}
              </span>
            ) : null}
            {publishTime !== undefined ? (
              <span>
                {typeof publishTime === 'string'
                  ? publishTime
                  : formatTime(publishTime * 1000)}
              </span>
            ) : null}
          </React.Fragment>
        )}
      </p>
    )
  }, [type, watchCount, publishTime, duration, linkWrapper])

  const hasHeader = useMemo(() => type === 'group' && groupId, [type, groupId])

  const Tag: any = tag || 'div'

  return (
    <Tag className={`${style['wrapper']} ${className || ''}`}>
      {header}
      <div onClick={onClick}>
        <div
          className={classNames('tw-relative tw-text-common-black')}
          style={{ fontSize: 0 }}
        >
          {linkWrapper(
            <React.Fragment>
              <Image
                width={coverWidth}
                height={coverHeight}
                src={coverUrl}
                alt={coverAlt || title}
                hoverTransition
                lazy={coverLazy}
                className={classNames(
                  hasHeader
                    ? 'tw-rounded-bl-2 tw-rounded-br-2'
                    : 'tw-rounded-4',
                  'g-load-img-wrap'
                )}
                layout={'responsive'}
                autoPixelRatio
              />
              {liveTag}
              <Duration
                duration={duration}
                className="tw-absolute tw-right-8 tw-bottom-8 tw-z-10"
              />
            </React.Fragment>
          )}
          {type === 'group' ? null : (
            <AuthorAvatar
              userId={userId}
              userVerified={userVerified}
              verifyType={verifyType}
              username={username}
              avatar={avatar}
              onClick={onClickUser}
            />
          )}
          {collectVisible ? (
            <CardCollectButton
              onClick={onClickCollect}
              id={id}
              collected={collected}
              className={hasHeader ? '' : 'tw-rounded-tr-4'}
            />
          ) : null}
        </div>
        <h3
          className={classNames(
            'tw-leading-22 tw-text-16 tw-font-medium tw-text-common-black tw-text-justify line-2 g-active-link-text',
            type === 'group' ? 'tw-mt-12' : 'tw-mt-36'
          )}
        >
          {linkWrapper(title)}
        </h3>
        {footer}
      </div>
    </Tag>
  )
}

export default CommonCard
